// leaderboardPanel.ts — the shared LEADERBOARD modal (opened from the Results screen / Menu).
//
// Tactical OLED redesign: a full-bleed modal (default scan rail) with a segmented scope control
// (Topic / Today / Week / All-Time), a kit listRow ladder (gold #1, the player's row washed amber
// + green), a "live" source note, and ONE primary footer — an amber SUBMIT SCORE when a fresh
// result is supplied (one-tap, remembered name), else a green DONE. Empty state: a dashed-ring
// podium mark + a "be the first" heading. Reads/writes go through src/net/leaderboard.ts, which
// transparently falls back to a localStorage board when the backend has no /scores route yet, so
// this UI is always functional. Pure UI; no Core/determinism touch.
import Phaser from 'phaser';
import { PALETTE, hex, FONT } from './keys';
import { modal } from './ui';
import { icon, type IconName } from './icons';
import { segmentedTabs, listRow, ctaButton, headingText, labelText } from './ui/kit';
import { scaledFont } from './settings';
import { audio } from './audio';
import {
  getTopScores,
  submitScore,
  getRememberedName,
  sanitizeName,
} from '../net/leaderboard';
import { putGhost } from '../net/ghostClient';
import type { LeaderboardScope, ScoreEntry, SubmitScoreInput } from '../net/types';
import { readLastGhost } from './lastGhost';
import { setMintedGhost } from './mintedGhost';

export interface LeaderboardPanelOpts {
  /** when present, render a SUBMIT row that posts this result (one-tap, remembered name). */
  submit?: SubmitScoreInput;
  /** the topic slug for the Topic tab (defaults to submit.topicNormalized). */
  topicNormalized?: string;
  /** open on this tab initially. */
  initialScope?: LeaderboardScope;
  /** Optional owner-scene callback for keyboard/input guards while the modal is open. */
  onClose?: () => void;
  /** Empty-state "Play Today's Gauntlet" CTA handler; defaults to just closing the panel. */
  onPlay?: () => void;
}

/** Scope tabs, in display order. Index <-> scope mapping for the segmented control. */
const TABS: ReadonlyArray<{ scope: LeaderboardScope; label: string }> = [
  { scope: 'topic', label: 'Topic' },
  { scope: 'daily', label: 'Today' },
  { scope: 'weekly', label: 'Week' },
  { scope: 'global', label: 'All-Time' },
];

/** Title-case a normalized topic slug for human display ("ancient-rome" -> "Ancient Rome"). */
function prettyTopic(slug: string): string {
  return slug
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function openLeaderboard(scene: Phaser.Scene, opts: LeaderboardPanelOpts = {}): ReturnType<typeof modal> {
  const desktop = scene.scale.width >= 900;
  // Full-bleed modal, default scan top-rail (no railGrad).
  const m = modal(scene, { title: 'LEADERBOARD', fullBleed: true, widthPx: desktop ? 980 : undefined, onClose: opts.onClose });
  const { card, cardW, bodyTop } = m;
  let listEmpty = false;
  const topicSlug = opts.topicNormalized ?? opts.submit?.topicNormalized ?? '';
  const topicLabel = opts.submit?.topicLabel || (topicSlug ? prettyTopic(topicSlug) : '');

  let scope: LeaderboardScope = opts.initialScope ?? (opts.submit?.isDaily ? 'daily' : topicSlug ? 'topic' : 'global');
  const hasSubmit = !!opts.submit;
  let submitted = false;
  let submitting = false;
  let confirmHint = '';
  // Where the player's pending score would land on the CURRENTLY VIEWED scope (1-based), recomputed
  // each time a list loads. Drives the single-CTA projected-rank hint ("#4 if you claim it").
  let projectedRank = 0;
  let existingYouRank = 0;

  const rowW = cardW - (desktop ? 48 : 36);

  const competitionName = (): string => {
    if (scope === 'topic') return 'This topic';
    if (scope === 'daily') return 'Today';
    if (scope === 'weekly') return 'This week';
    return 'All-time';
  };

  const contextLine = (): string => {
    if (scope === 'topic') return topicLabel ? `Beat nearby rivals on ${topicLabel}` : 'Beat nearby rivals on this topic.';
    if (scope === 'daily') return 'Fresh board, fresh ladder.';
    if (scope === 'weekly') return "This week's league — resets Monday.";
    return 'Long-haul legends live here.';
  };

  const emptyHeading = (): string => {
    if (scope === 'topic' && !topicSlug) return 'Pick a topic first';
    if (scope === 'daily') return 'No scores today — yet';
    if (scope === 'weekly') return 'No scores this week — yet';
    if (scope === 'topic') return 'No scores here — yet';
    return 'No scores yet — be the first';
  };

  // ── scope tabs ────────────────────────────────────────────────────────────
  const tabsY = bodyTop + 12;
  const initialIdx = Math.max(0, TABS.findIndex((t) => t.scope === scope));
  // segmentedTabs adds its container to the scene root; re-parent it under the card so it travels
  // with the modal (add() re-parents) and inherits the entrance tween / depth.
  const tabsCtl = segmentedTabs(
    scene,
    0,
    tabsY,
    cardW - 40,
    TABS.map((t) => t.label),
    (i) => {
      const next = TABS[i]?.scope ?? 'global';
      if (next === scope) return;
      scope = next;
      void loadList();
    },
    initialIdx,
  );
  card.add(tabsCtl.container);

  // ── subtitle ──────────────────────────────────────────────────────────────
  const subtitleY = tabsY + 36;
  const subtitle = scene.add
    .text(0, subtitleY, contextLine(), {
      fontFamily: FONT.family,
      fontSize: `${scaledFont(14, 11)}px`,
      color: hex(PALETTE.dim),
      align: 'center',
      wordWrap: { width: cardW - 56 },
    })
    .setOrigin(0.5);
  card.add(subtitle);
  const arenaMeta = scene.add.container(0, 0);
  card.add(arenaMeta);

  // ── list region ───────────────────────────────────────────────────────────
  const listRegionTop = subtitleY + (desktop ? 28 : 58);
  // Empty / loading cluster sits just UNDER the subtitle (not centred in a tall region) so a hugged
  // few-row card reads tight, not floating.
  const statusY = listRegionTop + 96;
  const rowH = desktop ? 48 : 52;
  const pitch = desktop ? 56 : 60;

  // S3b gap 3 — CONTENT-HUGGING. The primary CTA flows just below the last row (footerAnchorY, in
  // card-local coords) and the card is resized to hug it, so a 3-row board no longer leaves a >50%
  // void. `hugTo(contentBottomLocal)` places the footer a fixed gap under the content and shrinks the
  // card bottom to match. Recomputed on every load (tab switch / async data) — cheap + jank-free.
  let footerAnchorY = m.footerY;
  const footerReserveAbove = hasSubmit ? (opts.submit?.isDaily ? 88 : 58) : 26;
  const hugTo = (contentBottomLocal: number): void => {
    footerAnchorY = contentBottomLocal + 26 + footerReserveAbove;
    m.relayout(footerAnchorY + 54 - bodyTop);
  };

  const listContainer = scene.add.container(0, 0);
  card.add(listContainer);

  const statusTxt = scene.add
    .text(0, statusY, '', {
      fontFamily: FONT.family,
      fontSize: `${scaledFont(14, 12)}px`,
      color: hex(PALETTE.dim),
      align: 'center',
      wordWrap: { width: cardW - 56 },
    })
    .setOrigin(0.5);
  card.add(statusTxt);

  /** Project where `submit.score` would rank on the just-loaded `entries` (1-based, into a 10-cap). */
  const computeProjectedRank = (entries: ScoreEntry[]): number => {
    if (!opts.submit) return 0;
    const mine = Math.max(0, Math.floor(opts.submit.score));
    let rank = 1;
    for (const e of entries) if (e.score > mine) rank += 1;
    return rank;
  };

  const renderArenaMeta = (entries: ScoreEntry[], live: boolean): void => {
    arenaMeta.removeAll(true);
    const metaY = desktop ? subtitleY : subtitleY + 30;
    const top = entries[0];
    const left = scene.add.container(-rowW / 2, metaY);
    left.add(icon(scene, scope === 'daily' ? 'flame' : scope === 'weekly' ? 'medal' : 'ranking', 10, 0, 20, scope === 'daily' ? PALETTE.armed : PALETTE.scan));
    left.add(labelText(scene, 28, 0, `${competitionName()} board`, { size: 11, color: PALETTE.scan, origin: 0 }));
    arenaMeta.add(left);
    if (top) {
      const right = scene.add.container(rowW / 2, metaY);
      const topScore = Math.max(0, Math.floor(top.score)).toLocaleString('en-US');
      const best = scene.add
        .text(0, 0, `BEST ${topScore}`, {
          fontFamily: FONT.display,
          fontSize: `${scaledFont(12, 11)}px`,
          color: hex(PALETTE.gold),
          fontStyle: 'bold',
        })
        .setOrigin(1, 0.5);
      right.add(best);
      right.add(icon(scene, 'crown', -best.width - 18, 0, 16, PALETTE.gold));
      arenaMeta.add(right);
    } else {
      const right = scene.add.container(rowW / 2, metaY);
      const source = scene.add
        .text(0, 0, 'NO SCORES YET', {
          fontFamily: FONT.display,
          fontSize: `${scaledFont(11, 10)}px`,
          color: hex(PALETTE.textDim),
          fontStyle: '600',
        })
        .setOrigin(1, 0.5);
      right.add(source);
      right.add(scene.add.circle(-source.width - 13, 0, 4, live ? PALETTE.correct : PALETTE.mute));
      arenaMeta.add(right);
    }
  };

  // Centred empty-state cluster: a dashed podium ring + a scope-aware heading + a dim sub. Added to
  // listContainer so it clears on the next load / tab-switch.
  const renderEmpty = (): void => {
    const noTopic = scope === 'topic' && !topicSlug;
    const ringY = statusY - 34;
    const ring = scene.add.graphics();
    ring.lineStyle(2, PALETTE.lineStrong, 0.9);
    const r = 42;
    const dashes = 18;
    for (let i = 0; i < dashes; i++) {
      const a0 = (i / dashes) * Math.PI * 2;
      const a1 = a0 + (Math.PI * 2 / dashes) * 0.55;
      ring.beginPath();
      ring.arc(0, ringY, r, a0, a1, false);
      ring.strokePath();
    }
    listContainer.add(ring);
    listContainer.add(icon(scene, 'ranking', 0, ringY, 38, PALETTE.mute));
    const heading = headingText(scene, 0, statusY + 38, emptyHeading(), { size: 20, origin: 0.5 });
    const sub = scene.add
      .text(0, statusY + 70, noTopic ? 'Play any topic to open its leaderboard.' : 'Set the pace and everyone else chases you.', {
        fontFamily: FONT.family,
        fontSize: `${scaledFont(13, 11)}px`,
        color: hex(PALETTE.dim),
        align: 'center',
        lineSpacing: 3,
        wordWrap: { width: cardW - 72 },
      })
      .setOrigin(0.5, 0);
    listContainer.add([heading, sub]);
  };

  const renderEntries = (entries: ScoreEntry[], live: boolean): void => {
    listContainer.removeAll(true);
    listEmpty = entries.length === 0;
    projectedRank = computeProjectedRank(entries);
    existingYouRank = entries.findIndex((e) => e.isYou) + 1;
    renderArenaMeta(entries, live);
    if (entries.length === 0) {
      statusTxt.setText('');
      renderEmpty();
      hugTo(statusY + 116); // dashed ring + heading + 2-line sub
      renderFooter();
      return;
    }
    statusTxt.setText('');
    // S3 item 14: podium ranks wear MEDAL colors (gold / silver / bronze) for #1–3, neutral dim below;
    // the YOU row is marked by its washed fill + border + green label (not by recoloring its rank), and
    // every non-YOU score uses ONE consistent value color — so gold stays the premium podium/CTA accent.
    const MEDAL = [PALETTE.gold, 0xc7cdd6, 0xcd7f4f];
    entries.forEach((e, i) => {
      const y = listRegionTop + rowH / 2 + i * pitch;
      const you = !!e.isYou;
      const rankColor = i < 3 ? MEDAL[i] : PALETTE.dim;
      const medalIcon: IconName | undefined = i === 0 ? 'crown' : i < 3 ? 'medal' : undefined;
      listContainer.add(
        listRow(scene, 0, y, rowW, {
          rank: `${i + 1}`,
          rankColor,
          iconName: medalIcon,
          iconColor: rankColor,
          label: displayName(e.name, you),
          labelColor: you ? PALETTE.correct : PALETTE.text,
          sublabel: desktop ? (i === 0 ? 'pace-setter' : you ? 'your best' : i < 3 ? 'podium chase' : undefined) : undefined,
          value: e.score.toLocaleString(),
          valueColor: i === 0 ? PALETTE.gold : you ? PALETTE.correct : PALETTE.player,
          height: rowH,
          fill: you ? PALETTE.tileHot : i === 0 ? PALETTE.surface2 : PALETTE.surface,
          border: you ? PALETTE.armed : i === 0 ? PALETTE.gold : PALETTE.line,
        }),
      );
    });
    // live source note: green dot + "This topic board — live"
    const noteY = listRegionTop + rowH / 2 + (entries.length - 1) * pitch + rowH / 2 + 22;
    const noteStr = `${competitionName()} board — ${live ? 'live' : 'saved on this device'}`;
    const noteTxt = scene.add
      .text(0, 0, noteStr, { fontFamily: FONT.family, fontSize: `${scaledFont(13, 11)}px`, color: hex(PALETTE.dim) })
      .setOrigin(0, 0.5);
    const noteW = 14 + noteTxt.width;
    noteTxt.setX(-noteW / 2 + 14);
    const dot = scene.add.circle(-noteW / 2 + 4, 0, 4, PALETTE.correct);
    const note = scene.add.container(0, noteY, [dot, noteTxt]);
    listContainer.add(note);
    hugTo(noteY + 12);
    renderFooter();
  };

  const loadList = async (): Promise<void> => {
    subtitle.setText(contextLine());
    statusTxt.setText('Loading…');
    listContainer.removeAll(true);
    try {
      const { entries, live } = await getTopScores({ scope, topicNormalized: topicSlug, limit: 10 });
      renderEntries(entries, live);
    } catch {
      renderArenaMeta([], false);
      statusTxt.setText('Could not load the leaderboard.');
    }
  };

  // ── footer (single primary CTA) ───────────────────────────────────────────
  // With a fresh result: a projected-rank hint (amber) over an amber SUBMIT SCORE button, flipping
  // to a confirmed green state after the post. Without a result: a lone green DONE.
  const footer = scene.add.container(0, 0);
  card.add(footer);

  const projectedHint = (): string =>
    existingYouRank > 0 ? `You hold #${existingYouRank} here` :
    projectedRank > 0 ? `#${projectedRank} if you claim it` : 'Submit to start the chase';

  const doSubmit = (): void => {
    if (!opts.submit || submitted || submitting) return;
    audio().playSfx('uiConfirm');
    // Prompt for a name (remembered after the first time). prompt() is the most reliable cross-device
    // text entry from a Phaser canvas and only appears on an explicit tap.
    let name = getRememberedName();
    if (!name) {
      try {
        name = window.prompt('Name for the leaderboard:', '') ?? '';
      } catch {
        name = '';
      }
      if (!name.trim()) return; // cancelled
    }
    submitting = true;
    renderFooter();
    void submitScore({ ...opts.submit, name }).then((res) => {
      submitting = false;
      submitted = true;
      // GHOST DUELS Slice B: submitting a NAMED daily score to the public board IS the opt-in surface, so
      // AFTER the submit resolves, upload this run's local ghost (fire-and-forget) under the submitted name.
      maybeUploadDailyGhost(name);
      // Prefer the server's authoritative rank; otherwise fall back to the projected one we showed.
      const rank = typeof res.rank === 'number' && res.rank > 0 ? res.rank : projectedRank;
      const where = res.live ? 'live' : 'saved';
      confirmHint = rank > 0 ? `Your best is ${where} — #${rank}` : `Your best is ${where}`;
      renderFooter();
      void loadList(); // refresh so the player sees their new rank highlighted
    });
  };

  /**
   * GHOST DUELS Slice B opt-in upload. Only for a DAILY submit, and only when the locally-recorded daily
   * ghost (trivia.lastGhost.v1) matches THIS submitted run (same day + seed + topic). Uploads the blob
   * under the submitted leaderboard name (capped 12) and, on success, stashes the returned id so the next
   * share from ResultScene mints &gr=<id>. Fire-and-forget + fully degradable — a null (backend down / opt
   * mismatch) just means no raceable ghost, and the static-footprint share still works exactly as today.
   */
  const maybeUploadDailyGhost = (rawName: string): void => {
    const sub = opts.submit;
    if (!sub?.isDaily) return;
    const g = readLastGhost();
    if (!g) return;
    // A topic-less envelope (degenerate naked-URL daily where the topic-of-day never resolved) would 400
    // server-side (the ghost topic is a required, non-empty pin) — skip the request rather than fire a
    // doomed one. Real launches (DAILY button / share links) always carry the topic.
    if (g.topic === '') return;
    const today = new Date().toISOString().slice(0, 10);
    if (g.day !== today || g.seed !== sub.seed || g.topic !== sub.topicNormalized) return;
    const ghostName = (sanitizeName(rawName) || 'Anonymous').slice(0, 12);
    void putGhost({
      v: 1,
      bv: g.bv,
      day: g.day,
      topic: g.topic,
      seed: g.seed,
      m: g.m,
      name: ghostName,
      score: g.score,
      durationMs: g.durationMs,
      track: g.track,
    }).then((res) => {
      if (res) setMintedGhost({ id: res.id, day: g.day, seed: g.seed, topic: g.topic });
    });
  };

  function renderFooter(): void {
    footer.removeAll(true);
    if (!hasSubmit) {
      // Empty state → "PLAY TODAY'S GAUNTLET" (the ref's call-to-action); otherwise a plain DONE.
      if (listEmpty) {
        footer.add(
          ctaButton(scene, 0, footerAnchorY, "Play Today's Gauntlet", () => (opts.onPlay ? opts.onPlay() : m.close()), {
            width: cardW - 44,
            icon: 'play',
          }),
        );
      } else {
        footer.add(ctaButton(scene, 0, footerAnchorY, 'Done', () => m.close(), { width: cardW - 44 }));
      }
      return;
    }
    // hint line above the button
    let hintStr: string;
    let hintColor: number;
    if (submitted) {
      hintStr = confirmHint;
      hintColor = PALETTE.correct;
    } else if (submitting) {
      hintStr = 'Saving your best…';
      hintColor = PALETTE.dim;
    } else {
      hintStr = projectedHint();
      hintColor = PALETTE.armed;
    }
    footer.add(
      scene.add
        .text(0, footerAnchorY - 54, hintStr, {
          fontFamily: FONT.display,
          fontSize: `${scaledFont(14, 12)}px`,
          color: hex(hintColor),
          fontStyle: 'bold',
          align: 'center',
          wordWrap: { width: cardW - 56 },
        })
        .setOrigin(0.5),
    );
    // GHOST DUELS Slice B disclosure: a daily submit ALSO shares the run as a raceable ghost. This is a
    // PRIVACY opt-in, so #62 raises it from 11px/dim to body size + primary text — legible, not a
    // fine-print afterthought (still the single-line consent surface; no separate consent UI).
    if (opts.submit?.isDaily && !submitted) {
      footer.add(
        scene.add
          .text(0, footerAnchorY - 84, 'Submitting also shares your run as a raceable ghost.', {
            fontFamily: FONT.family,
            fontSize: `${scaledFont(13, 12)}px`,
            color: hex(PALETTE.text),
            align: 'center',
            wordWrap: { width: cardW - 56 },
          })
          .setOrigin(0.5),
      );
    }
    const label = submitting ? 'Submitting…' : submitted ? 'On the Board' : existingYouRank > 0 ? 'Update Best' : 'Submit Score';
    const fill = submitted ? PALETTE.correct : submitting ? PALETTE.mute : PALETTE.armed;
    footer.add(
      ctaButton(scene, 0, footerAnchorY, label, doSubmit, {
        width: cardW - 44,
        fill,
        ink: PALETTE.ink,
        glow: submitting ? null : fill,
        icon: submitted ? 'check' : 'arrowUp', // up-arrow on SUBMIT SCORE (matches the reference)
      }),
    );
  }

  renderFooter();
  void loadList();
  return m;
}

function truncate(s: string, n: number): string {
  return s.length > n ? `${s.slice(0, n - 1)}…` : s;
}

function displayName(name: string, you: boolean): string {
  if (!you) return truncate(name, 14);
  const clean = truncate(name, 12);
  return clean.toUpperCase() === 'YOU' ? 'YOU' : `${clean} · YOU`;
}
